import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import Box from '@client/uikit/Box';
import Frame from '@client/uikit/Frame';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import webViewStore from '@client/store/webViewStore';
import { useState } from 'react';
import _ from 'lodash';
import { BridgeHandlerDef, BridgeResponse } from '@type/bridgeType';
import { twMerge } from 'tailwind-merge';

class ViewStore {
    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {}
}

const BridgeTestBox = observer(
    ({
        title,
        description,
        buttons,
    }: {
        title: string;
        description: string;
        buttons: {
            label: string;
            onClick: () => Promise<BridgeResponse<keyof BridgeHandlerDef>>;
        }[];
    }) => {
        const [code, setCode] = useState<string>('');
        const [response, setResponse] = useState<BridgeResponse<keyof BridgeHandlerDef> | null>(
            null,
        );
        const [isExpanded, setIsExpanded] = useState<boolean>(false);

        function formatIndentation(code: string, indentSize = 2): string {
            // 줄로 나눔
            const lines = code.split('\n');

            // 첫 줄과 마지막 줄은 제거 불필요함
            lines.shift();
            lines.pop();

            // 각 줄의 앞쪽 공백(스페이스) 개수 구하기 (빈 줄 제외)
            const indents = lines
                .filter((line) => line.trim().length > 0)
                .map((line) => line.match(/^ */)?.[0].length ?? 0);

            // 가장 작은 들여쓰기 수(= 베이스 레벨)
            const minIndent = Math.min(...indents);

            return lines
                .map((line) => {
                    // 현재 줄의 앞쪽 공백 개수
                    const spaces = line.match(/^ */)?.[0].length ?? 0;
                    // 베이스 레벨을 뺀 후 2칸 단위로 환산
                    const level = Math.floor((spaces - minIndent) / indentSize);
                    // 새로 맞출 공백
                    const newIndent = ' '.repeat(Math.max(0, level) * indentSize);
                    return newIndent + line.trimStart();
                })
                .join('\n');
        }

        return (
            <Box>
                <div
                    className="relative"
                    onClick={() => {
                        if (!isExpanded) {
                            setIsExpanded(true);
                        }
                    }}
                >
                    <Box.Title title={title} />
                    {/* 우측 상단에 고정된 접기/펼치기 버튼 */}
                    <button
                        className="btn btn-sm btn-ghost text-base-content/30 absolute -top-2 right-0"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? <ChevronDownIcon /> : <ChevronUpIcon />}
                    </button>
                </div>
                <p className="text-base-content/70 text-sm">{description}</p>
                {isExpanded && (
                    <>
                        <div className="flex flex-row justify-between gap-2">
                            {buttons.map((button) => (
                                <button
                                    key={button.label}
                                    className="btn btn-sm grow"
                                    onClick={async () => {
                                        // 함수 코드를 들여쓰기 정리해서 표시 (2칸씩 정리)
                                        const functionStr = button.onClick.toString().trim();
                                        const formattedCode = formatIndentation(functionStr);
                                        setCode(formattedCode);

                                        const response = await button.onClick();
                                        setResponse(response);
                                    }}
                                >
                                    {button.label}
                                </button>
                            ))}
                        </div>
                        {code && (
                            <pre className="bg-base-200 overflow-x-auto rounded-lg p-3 text-sm">
                                <code>{code}</code>
                            </pre>
                        )}
                        {response && (
                            <div
                                className={twMerge(
                                    'text-base-content/70 overflow-x-auto text-sm',
                                    response.success ? 'text-success' : 'text-error',
                                )}
                            >
                                <pre>{JSON.stringify(response, null, 2)}</pre>
                            </div>
                        )}
                    </>
                )}
            </Box>
        );
    },
);

const BridgeTestPage = observer(() => {
    const viewStore = useMobxInstance(ViewStore);

    return (
        <Frame>
            <Frame.Title title="브릿지 테스트" />
            <Box>
                <Box.Title title="Bridge" />
                <div>Bridge version: {webViewStore.bridgeVersion}</div>
                <div>Bridge ready: {webViewStore.isWebView ? 'true' : 'false'}</div>
                <div>버튼을 누르면 메시지가 전송되고 결과가 표시됨 (result 가 앱의 응답)</div>
                <button
                    className="btn btn-sm"
                    onClick={() => {
                        location.reload();
                    }}
                >
                    페이지 새로고침 (새로고친 후 브릿지 생성 테스트)
                </button>
            </Box>
            <BridgeTestBox
                title="핑퐁"
                description="브릿지 핑퐁 테스트"
                buttons={[
                    {
                        label: '핑퐁',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('hello', {
                                name: _.uniqueId('test'),
                            });
                        },
                    },
                ]}
            />
            <BridgeTestBox
                title="유효하지 않은 요청"
                description="존재하지 않는 메서드 호출"
                buttons={[
                    {
                        label: '유효하지 않은 요청',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('invalidRequest' as any);
                        },
                    },
                ]}
            />
            <BridgeTestBox
                title="디바이스 정보"
                description="디바이스 정보 가져오기"
                buttons={[
                    {
                        label: '디바이스 정보',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('getDeviceInfo');
                        },
                    },
                ]}
            />
            <BridgeTestBox
                title="웹뷰"
                description="웹뷰 열기"
                buttons={[
                    {
                        label: '내부 도메인 웹뷰',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('openWebView', {
                                url: `https://${window.location.hostname}/news`,
                            });
                        },
                    },
                    {
                        label: '외부 도메인 웹뷰',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('openWebView', {
                                url: `https://www.google.com`,
                            });
                        },
                    },
                ]}
            />
            <BridgeTestBox
                title="인증 정보"
                description="me & 인증 토큰 가져오기"
                buttons={[
                    {
                        label: 'getMe',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('getMe');
                        },
                    },
                    {
                        label: 'getIdToken',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('getIdToken');
                        },
                    },
                    {
                        label: 'refreshToken',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('refreshToken');
                        },
                    },
                ]}
            />
            <BridgeTestBox
                title="로그인/로그아웃"
                description="구글로 로그인"
                buttons={[
                    {
                        label: '로그인',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('loginWithGoogle');
                        },
                    },
                    {
                        label: '로그아웃',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('logout');
                        },
                    },
                ]}
            />
            <BridgeTestBox
                title="다른 웹뷰에 이벤트 전파"
                description="다른 웹뷰에 이벤트 전파"
                buttons={[
                    {
                        label: '다른 웹뷰에 이벤트 전파',
                        onClick: async () => {
                            return await webViewStore.publishToOtherWebViews('bridge-test');
                        },
                    },
                ]}
            />
            <BridgeTestBox
                title="햅틱"
                description="햅틱 효과주기"
                buttons={[
                    {
                        label: 'Light',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('haptic', {
                                style: 'light',
                            });
                        },
                    },
                    {
                        label: 'Medium',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('haptic', {
                                style: 'medium',
                            });
                        },
                    },
                    {
                        label: 'Heavy',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('haptic', {
                                style: 'heavy',
                            });
                        },
                    },
                ]}
            />
            <BridgeTestBox
                title="공유"
                description="URL 공유하기"
                buttons={[
                    {
                        label: '공유',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('share', {
                                url: 'https://www.google.com',
                            });
                        },
                    },
                ]}
            />
            <BridgeTestBox
                title="앱 발송 메시지"
                description="앱 주도로 현재 웹뷰에 메시지 보내기"
                buttons={[
                    {
                        label: '앱에 메시지 보내기 요청',
                        onClick: async () => {
                            return await webViewStore.sendMessageToApp('appMessageTest', {
                                eventName: 'push-received',
                                eventData: {
                                    message: {
                                        notification: {
                                            title: '푸시 왔을 때 제목',
                                            body: '푸시 왔을 때 내용',
                                        },
                                        data: {
                                            link: 'haewao://news',
                                            campaignKey: 'test-1234567890',
                                        },
                                    },
                                },
                            });
                        },
                    },
                ]}
            />
        </Frame>
    );
});

export default BridgeTestPage;
